import { fieldsOf, identifier, isPlainRecord, listed, oneOf, pathTo, report, ticks, whole, type Place } from "./checks.ts";
import { SPEED_UNITS } from "../movement/true-point.ts";
import { QUARTER_RATE_TICKS } from "../protocol/limits.ts";
import {
  BASIS_POINTS,
  CONSTANT_COUNT_IDS,
  MAX_DISCOVERY_XP,
  MAX_OPENING_THROWS,
  MAX_PVP_LEVEL,
  MAX_REGENERATION_DELAY_TICKS,
  MAX_RUN_DRAIN,
  MAX_SPEED_UNITS,
  MIN_SPEED_UNITS,
  MAX_UNPAID_BLOW_PERCENT,
  MAX_TRAVEL_COPPER,
  MAX_XP_ABOVE_CAP,
  MAX_XP_PERCENT,
  MIN_XP_PERCENT,
  CONSTANT_IDS,
  type CountConstantId,
  type TickConstantId,
  CRIT_IDS,
  MAX_DAMAGE_PERCENT,
  REGENERATION_UNITS,
  RESOURCE_IDS,
  type Constant,
  type Crit,
  type Level,
  type RegenerationRate,
  type Resource,
} from "./types.ts";

const PERCENT = 100;

/**
 * The record checks of the levels, resources, constants and crits tables (ADR-069 decisions 3 and 5), read as
 * `records.ts` reads the abilities and weapons.
 */

/**
 * Checks a level: its number, the total XP that reaches it, its damage in whole percents (1 to `MAX_DAMAGE_PERCENT`)
 * and its pools, each pool at least 1. How the rows climb is the table's check (`checkLevelsClimb`).
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The level, or null.
 */
export function levelOf(place: Place, record: Readonly<Record<string, unknown>>): Level | null {
  const fields = fieldsOf(place, record, "", ["level", "xp", "damagePercent", "pools"], "a level") as Readonly<Record<string, unknown>>;
  const level = whole(place, fields["level"], "level", 1);
  const xp = whole(place, fields["xp"], "xp", 0);
  const damagePercent = whole(place, fields["damagePercent"], "damagePercent", 1, MAX_DAMAGE_PERCENT);
  const pools = fieldsOf(place, fields["pools"], "pools", RESOURCE_IDS, "the pools");
  if (pools === null) {
    return null;
  }
  const health = whole(place, pools["health"], "pools.health", 1);
  const stamina = whole(place, pools["stamina"], "pools.stamina", 1);
  const focus = whole(place, pools["focus"], "pools.focus", 1);
  if (level === null || xp === null || damagePercent === null || health === null || stamina === null || focus === null || Object.keys(pools).length !== RESOURCE_IDS.length || Object.keys(fields).length !== 4) {
    return null;
  }
  return { level, xp, damagePercent, pools: { health, stamina, focus } };
}

/**
 * Checks one rate of regeneration: a whole amount of points or percents, every whole number of ticks.
 *
 * @param place Where to report.
 * @param value The rate.
 * @param path Its path.
 * @returns The rate, or null.
 */
function rateOf(place: Place, value: unknown, path: string): RegenerationRate | null {
  const rate = fieldsOf(place, value, path, ["amount", "unit", "everyTicks"], "a rate");
  if (rate === null) {
    return null;
  }
  const unit = oneOf(place, rate["unit"], pathTo(path, "unit"), REGENERATION_UNITS, "a unit");
  const amount = whole(place, rate["amount"], pathTo(path, "amount"), 0, unit === "percent" ? PERCENT : undefined);
  const everyTicks = ticks(place, rate["everyTicks"], pathTo(path, "everyTicks"), 1);
  return amount === null || unit === null || everyTicks === null || Object.keys(rate).length !== 3 ? null : { amount, unit, everyTicks };
}

/**
 * Checks the pause a spend starts (ADR-117 decision 2): null for none, or a whole number of ticks from 1 to
 * `MAX_REGENERATION_DELAY_TICKS`.
 *
 * @param place Where to report.
 * @param value The value.
 * @param path Its path.
 * @returns The ticks, null for no pause, or `undefined` when the value is missing or refused.
 */
function delayOf(place: Place, value: unknown, path: string): number | null | undefined {
  if (value === null) {
    return null;
  }
  return ticks(place, value, path, 1, MAX_REGENERATION_DELAY_TICKS) ?? undefined;
}

/**
 * Checks a resource: an id the game knows, its regeneration in combat and out of it, and the pause a spend of it
 * starts.
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The resource, or null.
 */
export function resourceOf(place: Place, record: Readonly<Record<string, unknown>>): Resource | null {
  const fields = fieldsOf(place, record, "", ["id", "regeneration"], "a resource") as Readonly<Record<string, unknown>>;
  let id = identifier(place, fields["id"], "id");
  if (id !== null && !(RESOURCE_IDS as readonly string[]).includes(id)) {
    report(place, `the id is not a resource the game knows (${listed(RESOURCE_IDS)})`);
    id = null;
  }
  const regeneration = fieldsOf(place, fields["regeneration"], "regeneration", ["inCombat", "outOfCombat", "delayTicks"], "the regeneration");
  if (regeneration === null) {
    return null;
  }
  const inCombat = rateOf(place, regeneration["inCombat"], "regeneration.inCombat");
  const outOfCombat = rateOf(place, regeneration["outOfCombat"], "regeneration.outOfCombat");
  const delayTicks = delayOf(place, regeneration["delayTicks"], "regeneration.delayTicks");
  return id === null || inCombat === null || outOfCombat === null || delayTicks === undefined || Object.keys(regeneration).length !== 3 || Object.keys(fields).length !== 2 ? null : { id: id as Resource["id"], regeneration: { inCombat, outOfCombat, delayTicks } };
}

/**
 * The bounds of each count constant: the death penalty's three from 0, a discovery's XP to `MAX_DISCOVERY_XP`, the three
 * XP rates from `MIN_XP_PERCENT` to `MAX_XP_PERCENT` (their order is `checkXpRates`'s), and the
 * unpaid blow's share from 1 to `MAX_UNPAID_BLOW_PERCENT`, since a share of 0 would deal nothing where every hit deals
 * at least 1; and the two speeds within `MIN_SPEED_UNITS` and `MAX_SPEED_UNITS`, a creature's a whole number of
 * sixteenths (`multipleOf`), since a creature's step follows no pattern (ADR-116 decision 31 and section G, G2); and the
 * run's speed from `MIN_SPEED_UNITS`, its drain from 1 to `MAX_RUN_DRAIN` and its restart from 1, the bounds that read
 * another constant or the levels being `checkRunSpeed`'s and `checkRunRestart`'s; and a creature's opening throws from
 * 0 to `MAX_OPENING_THROWS`; and the PvP level from 1 to `MAX_PVP_LEVEL`; and the levels a creature may stand above a party mate who dealt no damage for the XP it pays that mate, from 0 to `MAX_XP_ABOVE_CAP`. A bound left out is the largest a rules number may be.
 */
const COUNT_BOUNDS: Readonly<Record<CountConstantId, { readonly min: number; readonly max?: number; readonly multipleOf?: number }>> = {
  death_gap_levels: { min: 0 },
  death_stacks_elite: { min: 0 },
  death_stacks_boss: { min: 0 },
  discovery_xp: { min: 0, max: MAX_DISCOVERY_XP },
  xp_percent_protected: { min: MIN_XP_PERCENT, max: MAX_XP_PERCENT },
  xp_percent_flagged: { min: MIN_XP_PERCENT, max: MAX_XP_PERCENT },
  xp_percent_hostile: { min: MIN_XP_PERCENT, max: MAX_XP_PERCENT },
  unpaid_blow_damage: { min: 1, max: MAX_UNPAID_BLOW_PERCENT },
  walk_speed: { min: MIN_SPEED_UNITS, max: MAX_SPEED_UNITS },
  creature_speed: { min: MIN_SPEED_UNITS, max: MAX_SPEED_UNITS, multipleOf: SPEED_UNITS },
  run_speed: { min: MIN_SPEED_UNITS },
  run_drain: { min: 1, max: MAX_RUN_DRAIN },
  run_restart: { min: 1 },
  creature_opening_throws: { min: 0, max: MAX_OPENING_THROWS },
  pvp_level: { min: 1, max: MAX_PVP_LEVEL },
  xp_above_cap: { min: 0, max: MAX_XP_ABOVE_CAP },
  party_share_percent: { min: 0, max: 100 },
  party_bonus_percent: { min: 0, max: 100 },
  party_bonus_max_percent: { min: 0, max: 100 },
  party_share_tiles: { min: 1, max: 255 },
  travel_coin_base: { min: 0, max: MAX_TRAVEL_COPPER },
  travel_coin_per_tile: { min: 0, max: MAX_TRAVEL_COPPER },
  travel_coin_cap: { min: 0, max: MAX_TRAVEL_COPPER },
  travel_focus_percent: { min: 0, max: 100 },
  travel_anywhere_focus_percent: { min: 0, max: 100 },
  travel_anywhere_focus_per_10: { min: 0, max: 100 },
  travel_anywhere_focus_cap: { min: 0, max: 100 },
  attribute_start: { min: 1, max: 100 },
  attribute_cap: { min: 1, max: 999 },
  attribute_points_per_level: { min: 0, max: 10 },
  attribute_vitality_health: { min: 0, max: 10000 },
  attribute_vitality_taken: { min: 0, max: 10000 },
  attribute_vitality_taken_max: { min: 0, max: 10000 },
  attribute_might_damage: { min: 0, max: 10000 },
  attribute_agility_speed: { min: 0, max: 10000 },
  attribute_agility_speed_max: { min: 0, max: 10000 },
  attribute_agility_cooldown: { min: 0, max: 10000 },
  attribute_agility_cooldown_max: { min: 0, max: 10000 },
  attribute_agility_dodge: { min: 0, max: 10000 },
  attribute_agility_dodge_max: { min: 0, max: 10000 },
  attribute_willpower_focus: { min: 0, max: 10000 },
  attribute_willpower_regeneration: { min: 0, max: 10000 },
  attribute_willpower_control: { min: 0, max: 10000 },
  attribute_willpower_control_max: { min: 0, max: 10000 },
  attribute_perception_crit: { min: 0, max: 10000 },
  attribute_perception_crit_floor: { min: 0, max: 10000 },
  attribute_perception_crit_max: { min: 0, max: 10000 },
  attribute_intellect_damage: { min: 0, max: 10000 },
  companion_fly_gap: { min: 1, max: 32 },
};

/**
 * The least each tick constant may hold where it is not 0: `jump_ticks` above the quarter rate's ticks, so two jumps of
 * one character never fall inside one quarter-rate view (ADR-116 decision 17).
 */
const TICK_MINIMUMS: Readonly<Partial<Record<TickConstantId, number>>> = {
  jump_ticks: QUARTER_RATE_TICKS + 1,
};

/**
 * Checks a constant: an id the code reads and its value, a whole number of ticks under `ticks`, at least its minimum
 * where it has one (`TICK_MINIMUMS`), or, for the counts
 * `CONSTANT_COUNT_IDS` names, a whole number under `count` within its bounds (`COUNT_BOUNDS`). A known constant holding
 * the other field is told which it holds, and nothing else of it is reported.
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The constant, or null.
 */
export function constantOf(place: Place, record: Readonly<Record<string, unknown>>): Constant | null {
  const named = isPlainRecord(record) && typeof record["id"] === "string" && (CONSTANT_IDS as readonly string[]).includes(record["id"]) ? (record["id"] as Constant["id"]) : null;
  const counted = named !== null && (CONSTANT_COUNT_IDS as readonly string[]).includes(named);
  const field = counted ? "count" : "ticks";
  const other = counted ? "ticks" : "count";
  if (named !== null && Object.hasOwn(record, other) && !Object.hasOwn(record, field)) {
    report(place, counted ? `${named} is a count, so it holds count, not ticks` : `${named} is a number of ticks, so it holds ticks, not count`);
    return null;
  }
  const fields = fieldsOf(place, record, "", ["id", field], "a constant") as Readonly<Record<string, unknown>>;
  let id = identifier(place, fields["id"], "id");
  if (id !== null && !(CONSTANT_IDS as readonly string[]).includes(id)) {
    report(place, `the id is not a constant the game reads (${listed(CONSTANT_IDS)})`);
    id = null;
  }
  const bounds = counted ? COUNT_BOUNDS[named as CountConstantId] : null;
  let value = bounds !== null ? whole(place, fields["count"], "count", bounds.min, bounds.max) : ticks(place, fields["ticks"], "ticks", (named === null ? undefined : TICK_MINIMUMS[named as TickConstantId]) ?? 0);
  if (value !== null && bounds !== null && bounds.multipleOf !== undefined && value % bounds.multipleOf !== 0) {
    report(place, `count is ${value}, and it is a multiple of ${bounds.multipleOf}: a whole number of sixteenths a step`);
    value = null;
  }
  if (id === null || value === null || Object.keys(fields).length !== 2) {
    return null;
  }
  return counted ? { id: id as CountConstantId, count: value } : { id: id as TickConstantId, ticks: value };
}

/**
 * Checks a crit: a crit the game rolls, its chance in basis points and its damage in whole percents, at least the
 * hit's own (ADR-073 decision 16).
 *
 * @param place Where to report.
 * @param record The record.
 * @returns The crit, or null.
 */
export function critOf(place: Place, record: Readonly<Record<string, unknown>>): Crit | null {
  const fields = fieldsOf(place, record, "", ["id", "chanceBasisPoints", "damagePercent"], "a crit") as Readonly<Record<string, unknown>>;
  let id = identifier(place, fields["id"], "id");
  if (id !== null && !(CRIT_IDS as readonly string[]).includes(id)) {
    report(place, `the id is not a crit the game rolls (${listed(CRIT_IDS)})`);
    id = null;
  }
  const chance = whole(place, fields["chanceBasisPoints"], "chanceBasisPoints", 0, BASIS_POINTS);
  const damagePercent = whole(place, fields["damagePercent"], "damagePercent", PERCENT);
  return id === null || chance === null || damagePercent === null || Object.keys(fields).length !== 3 ? null : { id: id as Crit["id"], chanceBasisPoints: chance, damagePercent };
}
